import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { Ref, ref, watch } from "vue";

import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { followLink } from "@/lib/openapi/helpers";

import { EntityType, useCurrentEntityManager } from "./entity-store";
import { useReleaseStore } from "./release-store";

export type BusinessEntity = types.BusinessEntity;
export type AttrEntry = { name: string; type: string; description: string };

// 'Manage' entities within a domain here
// Uses generated openapi client
// Uses wrapper to automatically init the store on first use or when enties are not set
export const useBusinessEntitiesStore = () => {
    const _store = defineStore("entities", () => {
        const toast = useToast();

        const entities: Ref<BusinessEntity[]> = ref([]);

        // Indicate busy state (request in transit)
        const isLoading: Ref<boolean> = ref(false);

        // Contain http response statusCode, only set when not 200
        const statusCode: Ref<number | undefined> = ref(undefined);
        const _error = ref();

        const { domain, stek } = useCurrentEntityManager();
        const releaseStore = useReleaseStore();

        const controller = ref(new AbortController());

        const getEntities = async () => {
            // Abort any previously running requests and create a new controller
            controller.value.abort();
            controller.value = new AbortController();
            if (!domain.value?.identity) {
                entities.value = [];
                return;
            }

            isLoading.value = true;

            try {
                const { data, response } =
                    await api.startDomainBusinessEntityMgtGetBusinessEntitiesStartPost(
                        {
                            body: {
                                domain_id: domain.value.identity,
                            },
                            signal: controller.value.signal,
                        }
                    );

                if (response.status === 200) {
                    statusCode.value = 200;
                    if (data) {
                        entities.value = data;
                    }
                } else {
                    //TODO talk about error handling
                    // Alternatively we could just try and catch the HttpError and expose it as a prop
                    statusCode.value = response.status;
                    _error.value = data;
                }
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isLoading.value = false;
            }
        };

        watch(
            () => domain.value?.identity,
            async () => {
                await getEntities();
            },
            { immediate: true }
        );

        // Re-fetch when the active revision (thread branch / release tag)
        // changes — a ?thread= query change does not alter route params, so the
        // domain watcher above would not fire on its own.
        watch(
            () => releaseStore.activeRevision,
            async () => {
                await getEntities();
            }
        );

        // Refresh the entities list whenever any business entity is synced,
        // so relationship changes from BusinessEntityPage propagate to the diagram.
        //
        // A BroadcastChannel does not deliver messages to the same object instance
        // that posted them, so we create a dedicated receiver instance here to ensure
        // messages from sync-store (which uses the shared syncChannel) are received
        // within the same tab as the backend creates the short description.
        const syncListenerChannel = new BroadcastChannel(syncChannel.name);
        syncListenerChannel.addEventListener(
            "message",
            ({ data: { message, identity } }) => {
                if (
                    message === "synced" &&
                    identity.entityType === EntityType.BUSINESS_ENTITY
                ) {
                    if (stek.value) {
                        getEntities();
                    }
                }
            }
        );

        watch(_error, () => {
            if (_error.value)
                toast.add({
                    severity: "error",
                    summary: "Error " + statusCode.value,
                    detail: _error.value.detail
                        ? _error.value.detail[0].msg
                        : _error.value,
                });
            _error.value = undefined;
        });

        const updateEntity = async (
            entity: Pick<BusinessEntity, "identity" | "name"> & {
                kind?: string | null;
            }
        ) => {
            if (!entity) {
                return;
            }

            isLoading.value = true;

            try {
                const { response } =
                    await api.startDomainBusinessEntityMgtUpdateBusinessEntityStartPost(
                        {
                            body: {
                                business_entity_id: entity.identity,
                                name: entity.name,
                                kind: entity.kind ?? undefined,
                            },
                        }
                    );
                statusCode.value = response.status;
                await getEntities();
            } catch (error) {
                statusCode.value = 400;
                _error.value = error;
            } finally {
                isLoading.value = false;
            }
        };

        const createEntity = async (
            name: string,
            description: string,
            kind?: string
        ) => {
            // name is required
            if (!name || !domain.value) {
                return;
            }
            isLoading.value = true;
            try {
                const { response } =
                    await api.startDomainBusinessEntityMgtCreateBusinessEntityStartPost(
                        {
                            body: {
                                domain_id: domain.value.identity,
                                name,
                                kind,
                                description: new File([description], name, {
                                    type: "text/markdown",
                                }),
                            },
                        }
                    );
                statusCode.value = response.status;
                await getEntities();
            } catch (error) {
                statusCode.value = 400;
                _error.value = error;
            } finally {
                isLoading.value = false;
            }
        };

        /**
         * Returns whether the object was actually deleted.
         *
         * Errors are caught here to raise the toast, so this never rejects —
         * callers that act on the outcome (closing a panel, navigating away)
         * must check the result rather than assume the await succeeding means
         * the delete did.
         */
        const deleteEntity = async (entityId: string): Promise<boolean> => {
            if (!entityId) {
                return false;
            }

            isLoading.value = true;
            try {
                const { response } =
                    await api.startDomainBusinessEntityMgtDeleteBusinessEntityStartPost(
                        {
                            body: {
                                business_entity_id: entityId,
                            },
                        }
                    );
                statusCode.value = response.status;
                await getEntities();
                return true;
            } catch (error) {
                statusCode.value = 400;
                _error.value = error;
                return false;
            } finally {
                isLoading.value = false;
            }
        };

        /**
         * Non-reactive cache so attribute fetches don't trigger a re-render loop.
         * Key: entity identity. Value: parsed attribute list (or [] when fetch failed).
         * Also stores the `attribute_count` at fetch time so stale entries are
         * invalidated when attributes are added/removed.
         */
        const attributeCache = new Map<
            string,
            { count: number; attrs: AttrEntry[] }
        >();

        const fetchAttributesForClassDiagram = async (
            entitiesToCheck: BusinessEntity[]
        ): Promise<void> => {
            const toFetch = entitiesToCheck.filter((entity) => {
                if (!entity.definition?.links) return false;
                const cached = attributeCache.get(entity.identity);
                return (
                    !cached || cached.count !== (entity.attribute_count ?? 0)
                );
            });

            if (toFetch.length === 0) return;

            await Promise.allSettled(
                toFetch.map(async (entity) => {
                    const count = entity.attribute_count ?? 0;
                    try {
                        const { response, data } = await followLink<
                            Record<string, { type?: string }>
                        >(entity.definition!, "get");

                        if (!response.ok) {
                            attributeCache.set(entity.identity, {
                                count,
                                attrs: [],
                            });
                            return;
                        }
                        const attrs = Object.entries(data ?? {}).map(
                            ([name, field]) => ({
                                name,
                                type: field?.type ?? "",
                                description: field?.description ?? "",
                            })
                        );
                        attributeCache.set(entity.identity, { count, attrs });
                    } catch {
                        attributeCache.set(entity.identity, {
                            count,
                            attrs: [],
                        });
                    }
                })
            );
        };

        const getEntityAttributes = (identity: string): AttrEntry[] =>
            attributeCache.get(identity)?.attrs ?? [];

        return {
            entities,
            isLoading,
            statusCode,
            getEntities,
            updateEntity,
            createEntity,
            deleteEntity,
            fetchAttributesForClassDiagram,
            getEntityAttributes,
        };
    });

    const store = _store();
    if (!store.entities) {
        store.getEntities();
    }
    return store;
};

export const useBusinessEntitiesManager = () =>
    storeToRefs(useBusinessEntitiesStore());
